import { useEffect, useState } from "react";
import {
  Checkbox,
  ListItemText,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../context/AuthProvider";
import styled from "@emotion/styled";
import { useGetQuery } from "../../hooks/useQuery";

const KarvesContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  margin-bottom: 4px;
`;

const Karves = ({ isLoading, savId, bandos, karves, changeKarves, handleZymVisasKarves }) => {
  const [zymVisos, setZymVisos] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [localKarves, setLocalKarves] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const auth = useAuth();
  const arSavininkas = auth.user.role === "Ūkis";

  const visuKarviuList = useGetQuery(arSavininkas ? "karves" : "karves/" + savId);

  // uzkrauna karves is localStorage
  useEffect(() => {
    if (visuKarviuList.length > 0 && !hasInitialized) {
      let selectedKarveNumbers = [];
      if (localStorage.getItem("buvesPuslapis") === "/karves-rodikliai") {
        const storedSelectedKarves = localStorage.getItem("selectedKarves");
        if (storedSelectedKarves) {
          try {
            const selectedKarvesList = JSON.parse(storedSelectedKarves);

            if (selectedKarvesList && selectedKarvesList.length > 0) {
              setLocalKarves(selectedKarvesList);

              selectedKarveNumbers = selectedKarvesList
                .map((karve) => karve.karNr)
                .filter((karNr) => visuKarviuList.some((cow) => cow.karNr === karNr));

              if (selectedKarveNumbers.length > 0) {
                // uzzymi karves is storage
                handleZymVisasKarves(selectedKarveNumbers);

                if (selectedKarveNumbers.length === visuKarviuList.length) {
                  setZymVisos(true);
                } else {
                  setZymVisos(false);
                }
              }
            }
          } catch (error) {
            console.error("Failed to parse selected karves from localStorage:", error);
          }
        }
      }

      setHasInitialized(true);
    }
  }, [visuKarviuList, hasInitialized, changeKarves, handleZymVisasKarves]);

  // Save selected cows to localStorage whenever karves changes
  useEffect(() => {
    if (hasInitialized && visuKarviuList.length > 0) {
      if (karves.length > 0) {
        // Find the complete cow objects for selected karves
        const selectedKarveObjects = visuKarviuList.filter((cow) => karves.includes(cow.karNr));

        // Save directly to dedicated storage
        localStorage.setItem("selectedKarves", JSON.stringify(selectedKarveObjects));
      } else {
        // If no cows are selected, clear the storage
        localStorage.setItem("selectedKarves", JSON.stringify([]));
      }
    }
  }, [karves, visuKarviuList, hasInitialized]);

  // Combine local and fetched data
  const karviuList =
    visuKarviuList.length > 0 ? visuKarviuList : localKarves.length > 0 ? localKarves : [];

  let banduKarves =
    bandos.length > 0 ? karviuList.filter((k) => bandos.includes(k.banda)) : karviuList;

  searchText !== "" && (banduKarves = banduKarves.filter((k) => k.karNr.includes(searchText)));

  const handleZymKarve = (target) => {
    changeKarves(target.checked, target.value);
    if (target.checked && searchText === "") {
      setZymVisos(karves.length === banduKarves.length);
    } else {
      setZymVisos(false);
    }
  };

  const handleZymVisas = (checked) => {
    if (checked) {
      setZymVisos(true);
      handleZymVisasKarves(banduKarves.map((karve) => karve.karNr));
    } else {
      setZymVisos(false);
      handleZymVisasKarves([]);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setZymVisos(false);
    changeKarves([]);
  };

  if (isLoading) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            marginBottom: "4px",
          }}
        >
          <Skeleton width="50%" height={30} />
          {[...Array(5).keys()].map((s) => (
            <Box key={s} sx={{ display: "flex", gap: "20px", paddingLeft: "30px" }}>
              <Skeleton width={30} height={30} />
              <Skeleton width="100%" />
            </Box>
          ))}
        </Box>
      </>
    );
  }

  const renderListItems = () => {
    const data = searchText === "" ? [{ karNr: "Žymėti visas" }, ...banduKarves] : banduKarves;

    return (
      <Grid container spacing={1} sx={{ maxHeight: 285, overflow: "auto" }}>
        {data.map((item, index) => (
          <Grid xs={12} lg={6} xl={4} key={index} width={220}>
            <ListItem component="div" disablePadding>
              <ListItemButton
                onClick={
                  index === 0 && searchText === ""
                    ? (e) => handleZymVisas(e.target.checked)
                    : (e) => handleZymKarve(e.target)
                }
                sx={{
                  border: "1px solid #eee",
                  borderRadius: 1,
                  minHeight: 50,
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={karves.indexOf(item.karNr) > -1 || zymVisos}
                    value={item.karNr}
                    inputProps={{ "aria-labelledby": item.karNr }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.karNr}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: { fontSize: { xs: "0.9rem", sm: "1rem" } },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (karviuList.length === 0) {
    return <Typography>Karvės</Typography>;
  }

  return (
    <>
      <KarvesContainer>
        <Typography>{karves ? "Karvės" : "Pasirinkite karves"}</Typography>
        <TextField
          id="karNr"
          name="karNr"
          type="text"
          value={searchText}
          size="small"
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            style: {
              borderRadius: "12px",
              height: "24px",
              paddingLeft: "10px",
            },
          }}
        />
      </KarvesContainer>
      {banduKarves.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography color="red">Karvių su tokiu numeriu nerasta</Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>{renderListItems()}</Box>
      )}
    </>
  );
};

export default Karves;
